import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissionContext } from '../../contexts/PermissionContext';
import './POSPage.css';
import apiService from '../../services/api';
import AddProduct from '../../AddProduct';
import AddSupplier from './AddSupplier';
import POSOpenSalesModal from './POSOpenSalesModal';
import RegisterDetailsModal from './RegisterDetailsModal';
import dateUtils from '../../utils/dateUtils';
import invoiceGenerator from '../../services/invoiceGenerator';

/* ── Toast ──────────────────────────────────────────── */
const Toast = ({ toasts, removeToast }) => (
  <div className="pos-toast-container">
    {toasts.map(t => (
      <div key={t.id} className={`pos-toast pos-toast--${t.type}`}>
        <span className="pos-toast-icon">
          {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}
        </span>
        <span>{t.message}</span>
        <button className="pos-toast-close" onClick={() => removeToast(t.id)}>×</button>
      </div>
    ))}
  </div>
);

/* ── Hold Sales Modal ──────────────────────────────────── */
const HoldSalesModal = ({ heldSales, setHeldSales, onRestore, onClose }) => {
  const [selectedHoldId, setSelectedHoldId] = useState(
    heldSales.length > 0 ? heldSales[0].id : null
  );

  const selectedSale = heldSales.find(h => h.id === selectedHoldId);

  const handleDeleteAll = () => {
    setHeldSales([]);
    onClose();
  };

  const handleDelete = () => {
    if (!selectedSale) return;
    setHeldSales(prev => prev.filter(h => h.id !== selectedSale.id));
    if (heldSales.length <= 1) onClose();
    else {
      const rest = heldSales.filter(h => h.id !== selectedSale.id);
      if (rest.length > 0) setSelectedHoldId(rest[0].id);
    }
  };

  const handleEditInCart = () => {
    if (!selectedSale) return;
    onRestore(selectedSale);
  };

  const getSubtotal = (cart) => cart.reduce((s, i) => s + (i.price * i.quantity), 0);
  const getTotalQty = (cart) => cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-hs-modal" onClick={e => e.stopPropagation()}>
        <div className="pos-hs-header">
          <h2>Hold Sales</h2>
          <button className="pos-hs-close" onClick={onClose}>×</button>
        </div>

        <div className="pos-hs-body">
          {/* Left Panel */}
          <div className="pos-hs-left">
            <div className="pos-hs-table-wrap">
              <table className="pos-hs-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Customer</th>
                    <th>Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  {heldSales.map((h, i) => (
                    <tr
                      key={h.id}
                      className={h.id === selectedHoldId ? 'active' : ''}
                      onClick={() => setSelectedHoldId(h.id)}
                    >
                      <td>{i + 1}</td>
                      <td>{h.customer || 'Walk-in Customer ()'}</td>
                      <td>{h.time}</td>
                    </tr>
                  ))}
                  {heldSales.length === 0 && (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>No hold sales found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button className="pos-hs-delete-all-btn" onClick={handleDeleteAll} disabled={heldSales.length === 0}>
              Delete all Hold Sales
            </button>
          </div>

          {/* Right Panel */}
          <div className="pos-hs-right">
            <div className="pos-hs-right-header">Sale Details</div>

            {selectedSale ? (
              <div className="pos-hs-details">
                <div className="pos-hs-customer-name">
                  Customer: {selectedSale.customer || 'Walk-in Customer'}
                </div>

                <div className="pos-hs-details-table-wrap">
                  <table className="pos-hs-details-table" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '45%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '15%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Item</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Discount</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSale.cart.map(item => (
                        <tr key={item.id}>
                          <td style={{ textAlign: 'left', wordWrap: 'break-word', whiteSpace: 'normal', overflowWrap: 'break-word' }}>
                            {item.name || item.product}
                          </td>
                          <td>{(item.price || 0).toFixed(2)}</td>
                          <td>{item.quantity}</td>
                          <td>0.00</td>
                          <td>{((item.price || 0) * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pos-hs-summary-grid">
                  <div>Sub Total: {getSubtotal(selectedSale.cart).toFixed(2)}</div>
                  <div>Total Item: {selectedSale.cart.length} ({getTotalQty(selectedSale.cart)})</div>
                  <div>Tax: 0.00</div>
                  <div>Charge: 0.00</div>
                  <div>Discount: 0.00 (0.00)</div>
                </div>

                <div className="pos-hs-total-payable">
                  Total Payable: {selectedSale.total.toFixed(2)}
                </div>

                <div className="pos-hs-action-buttons">
                  <button className="pos-hs-btn-edit" onClick={handleEditInCart}>Edit in Cart</button>
                  <button className="pos-hs-btn-delete" onClick={handleDelete}>Delete</button>
                  <button className="pos-hs-btn-cancel" onClick={onClose}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>Select a sale to view details</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Sale Refund Modal ──────────────────────────────────── */
const SaleRefundModal = ({ onClose }) => {
  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-refund-modal" onClick={e => e.stopPropagation()}>
        <div className="pos-refund-header">
          <h2>Sale Refund</h2>
          <button className="pos-refund-close" onClick={onClose}>×</button>
        </div>

        <div className="pos-refund-body">
          <div className="pos-refund-input-group">
            <label>Invoice Number *</label>
            <input type="text" className="pos-refund-input" required />
          </div>

          <div className="pos-refund-input-group">
            <label>Customer Name</label>
            <input type="text" className="pos-refund-input pos-refund-input-disabled" disabled />
          </div>

          <div className="pos-refund-input-group">
            <label>Refund Reason *</label>
            <textarea className="pos-refund-textarea" required></textarea>
          </div>

          <div className="pos-refund-items-section">
            <div className="pos-refund-subtitle">Select Items to Return:</div>

            <div className="pos-refund-totals">
              <div>Total Paid: <strong>0.00</strong></div>
              <div>Refund Amount: <strong>0.00</strong></div>
            </div>

            <button className="pos-refund-process-btn">Process Return</button>

            <div className="pos-refund-table-wrap">
              <table className="pos-refund-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Return Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Empty state until invoice is loaded */}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="pos-refund-footer">
          <button className="pos-refund-cancel-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── Discount Coupon Modal ─────────────────────────────── */
const DiscountCouponModal = ({ code, setCode, onClose, onSubmit }) => {
  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-coupon-modal" onClick={e => e.stopPropagation()}>
        <div className="pos-coupon-header">
          <h2>Discount coupon (on entire sale)</h2>
        </div>
        <div className="pos-coupon-body">
          <label>Coupon Code</label>
          <input 
            type="text" 
            placeholder="Coupon Code" 
            className="pos-coupon-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </div>
        <div className="pos-coupon-footer">
          <button className="pos-coupon-btn pos-coupon-btn--submit" onClick={onSubmit}>Submit</button>
          <button className="pos-coupon-btn pos-coupon-btn--cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── Finalize Sale Modal ──────────────────────────────────── */
const PaymentModal = ({ total, rawTotal, onClose, onConfirm, type, customer, addToast }) => {
  const safeTotal = parseFloat(total || 0);
  const safeRawTotal = parseFloat(rawTotal || safeTotal);

  const [activeMethod, setActiveMethod] = useState(
    type === 'card' ? 'Credit Card' : 'Cash'
  );

  const [givenAmount, setGivenAmount] = useState('');
  const [amount, setAmount] = useState(safeTotal.toFixed(2));
  const [payments, setPayments] = useState([]); // multi-tender list
  const [showCurrency, setShowCurrency] = useState(false);

  // Bank fields
  const [bankName, setBankName] = useState('');
  const [checkNo, setCheckNo] = useState('');
  const [checkIssueDate, setCheckIssueDate] = useState('');
  const [checkExpiryDate, setCheckExpiryDate] = useState('');

  // Manual Credit fields
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');

  // Loyalty Point
  const [loyaltyPts, setLoyaltyPts] = useState('');

  // Invoice checkboxes
  const [sendSMS, setSendSMS] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const due = Math.max(0, safeTotal - totalPaid);
  const calcChange = Math.max(0, (parseFloat(givenAmount) || 0) - (parseFloat(amount) || 0)).toFixed(2);

  const handleAdd = () => {
    const val = parseFloat(activeMethod === 'Loyalty Point' ? loyaltyPts : amount) || 0;
    if (val <= 0) return;
    setPayments(prev => [...prev, { method: activeMethod, amount: val }]);
    setAmount(Math.max(0, due - val).toFixed(2)); // auto-deduct remaining due
    setGivenAmount('');
    setLoyaltyPts('');
  };

  const removePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
    // Reset amount to the new due balance
    const newTotalPaid = payments.filter((_, i) => i !== index).reduce((s, p) => s + p.amount, 0);
    setAmount(Math.max(0, safeTotal - newTotalPaid).toFixed(2));
  };

  const handleQuickKey = (val) => {
    setGivenAmount(val.toFixed(2));
  };

  const handleClear = () => { setAmount(safeTotal.toFixed(2)); setGivenAmount(''); setPayments([]); };

  const METHODS = ['Cash', 'Credit Card', 'Loyalty Point', 'Bank', 'Manual Credit'];

  const renderTopInputs = () => {
    const changeVal = (parseFloat(givenAmount) || 0) > 0 
      ? Math.max(0, (parseFloat(givenAmount) || 0) - (totalPaid || parseFloat(amount || 0))).toFixed(2)
      : '0.00';

    return (
      <>
        <div className="pos-pm-top-inputs-grid" style={{ marginBottom: '24px' }}>
          <div className="pos-pm-input-col">
            <label>Given Amount</label>
            <input type="number"
              value={givenAmount}
              onChange={e => setGivenAmount(e.target.value)}
              className="pos-pm-amount-input pos-pm-border-red"
              placeholder="Given Amount" />
          </div>
          <div className="pos-pm-input-col">
            <label>Change Amount</label>
            <input type="number"
              value={changeVal}
              readOnly
              className="pos-pm-amount-input pos-pm-readonly"
              placeholder="Change Amount" />
          </div>
          <div className="pos-pm-input-col">
            <label>Amount</label>
            <input type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="pos-pm-amount-input"
              placeholder="Amount" />
          </div>
          <div className="pos-pm-input-col pos-pm-align-bottom">
            <button className="pos-pm-add-btn" onClick={handleAdd}>Add</button>
          </div>
        </div>

        {/* Payments List */}
        <div className="pos-pm-payments-list">
          {payments.map((p, i) => (
            <div key={i} className="pos-pm-payment-item">
              <span className="pos-pm-payment-method">{p.method}</span>
              <span className="pos-pm-payment-amount">{p.amount.toFixed(2)}</span>
              <button className="pos-pm-payment-remove" onClick={() => removePayment(i)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
              </button>
            </div>
          ))}
        </div>

        {/* Large Change Amount Display */}
        {totalPaid > 0 && (parseFloat(givenAmount) || 0) > 0 && (
          <div className="pos-pm-change-large">
            <div className="pos-pm-change-label">Change Amount</div>
            <div className="pos-pm-change-value">{(parseFloat(givenAmount || 0) - totalPaid).toFixed(2)}</div>
          </div>
        )}

        {showCurrency && (
          <div className="pos-pm-top-inputs-grid" style={{ marginBottom: '24px' }}>
            <div className="pos-pm-input-col">
              <select className="pos-pm-amount-input">
                <option>Change Currency</option>
                <option>USD - US Dollar</option>
                <option>EUR - Euro</option>
              </select>
            </div>
            <div className="pos-pm-input-col"></div>
            <div className="pos-pm-input-col" style={{ flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
              <input type="number" className="pos-pm-amount-input" placeholder="Amount" style={{ backgroundColor: '#f8fafc' }} />
              <button className="pos-pm-trash-btn" onClick={() => setShowCurrency(false)} title="Remove Currency Override">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2M10 11v6M14 11v6" /></svg>
              </button>
            </div>
            <div className="pos-pm-input-col"></div>
          </div>
        )}
      </>
    );
  };

  const renderForm = () => {
    if (activeMethod === 'Loyalty Point') return (
      <div className="pos-pm-top-inputs-grid pos-pm-loyalty-grid">
        <div className="pos-pm-input-col" style={{ flex: 2 }}>
          <label>Loyalty Point</label>
          <input type="number" className="pos-pm-amount-input" placeholder="Loyalty Point"
            value={loyaltyPts} onChange={e => setLoyaltyPts(e.target.value)} />
        </div>
        <div className="pos-pm-input-col pos-pm-align-bottom" style={{ flex: 1 }}>
          <button className="pos-pm-add-btn" onClick={handleAdd}>Add</button>
        </div>
      </div>
    );
    if (activeMethod === 'Bank') return (
      <>
        {renderTopInputs()}
        <div className="pos-pm-bank-fields">
          <div><div className="pos-pm-field-lbl">Bank Name</div>
            <input className="pos-pm-field-inp" placeholder="Bank Name" value={bankName} onChange={e => setBankName(e.target.value)} /></div>
          <div><div className="pos-pm-field-lbl">Check No</div>
            <input className="pos-pm-field-inp" placeholder="Check No" value={checkNo} onChange={e => setCheckNo(e.target.value)} /></div>
          <div><div className="pos-pm-field-lbl">Check Issue Date</div>
            <input type="date" className="pos-pm-field-inp" value={checkIssueDate} onChange={e => setCheckIssueDate(e.target.value)} /></div>
          <div><div className="pos-pm-field-lbl">Check Expiry Date</div>
            <input type="date" className="pos-pm-field-inp" value={checkExpiryDate} onChange={e => setCheckExpiryDate(e.target.value)} /></div>
        </div>
      </>
    );
    if (activeMethod === 'Manual Credit') return (
      <>
        {renderTopInputs()}
        <div className="pos-pm-manual-fields">
          <div><div className="pos-pm-field-lbl">Card Holder Name</div>
            <input className="pos-pm-field-inp" placeholder="Card Holder Name" value={cardHolder} onChange={e => setCardHolder(e.target.value)} /></div>
          <div><div className="pos-pm-field-lbl">Card Holding Number</div>
            <input className="pos-pm-field-inp" placeholder="Card Holding Number" value={cardNumber} onChange={e => setCardNumber(e.target.value)} /></div>
        </div>
      </>
    );
    // Cash / Credit Card
    return (
      <>
        {renderTopInputs()}
        {activeMethod === 'Credit Card' && (
          <button className="pos-pm-make-payment-btn" onClick={handleAdd}>Make Payment</button>
        )}
      </>
    );
  };

  return (
    <div className="pos-pm-overlay" onClick={onClose}>
      <div className="pos-pm-modal" onClick={e => e.stopPropagation()}>

        <div className="pos-pm-header">
          <h2>Finalize Sale</h2>
          <button className="pos-pm-close" onClick={onClose}>×</button>
        </div>

        <div className="pos-pm-body">
          {/* Left sidebar */}
          <div className="pos-pm-sidebar">
            <div className="pos-pm-sidebar-title">Payment Method</div>
            <div className="pos-pm-methods">
              {METHODS.map(m => (
                <button key={m}
                  disabled={m === 'Credit Card'}
                  style={m === 'Credit Card' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  className={`pos-pm-method-btn ${activeMethod === m ? 'active' : ''}`}
                  onClick={() => { 
                    setActiveMethod(m); 
                    setAmount(due.toFixed(2)); 
                  }}
                >{m}</button>
              ))}
            </div>
            <button className="pos-pm-currency-btn" onClick={() => setShowCurrency(prev => !prev)}>Change Currency</button>
          </div>

          {/* Right main */}
          <div className="pos-pm-main">
            {/* Method title + customer */}
            <div className="pos-pm-top-info">
              <div>
                <div className="pos-pm-method-title">{activeMethod}</div>
                <div className="pos-pm-method-sub">
                  {activeMethod === 'Loyalty Point' ? 'Loyalty Point' : 'Amount'}
                </div>
              </div>
              <div className="pos-pm-customer-info">
                {activeMethod === 'Loyalty Point' ? (
                  <span className="pos-pm-balance">Available Loyalty Point: 0</span>
                ) : (
                  <>
                    <span>Customer: {customer?.name || 'Walk-in Customer'}</span>
                    <span className="pos-pm-balance">Opening Balance: 5.00 (Debit)</span>
                  </>
                )}
              </div>
            </div>

            {/* Dynamic form per method */}
            {renderForm()}

            {/* Summary + Quick Keys */}
            <div className="pos-pm-mid-grid">
              <div className="pos-pm-summary">
                <div className="pos-pm-summary-row"><span className="label">Subtotal</span><span className="val">{safeRawTotal.toFixed(2)}</span></div>
                <div className="pos-pm-summary-row total"><span className="label">Grand Total</span><span className="val">{safeTotal.toFixed(2)}</span></div>
                <div className="pos-pm-summary-row"><span className="label">Paid</span><span className="val">{totalPaid.toFixed(2)}</span></div>
                <div className="pos-pm-summary-row highlight"><span className="label">Due</span><span className="val">{due.toFixed(2)}</span></div>
                <button className="pos-pm-cart-details-btn">Cart Details</button>
              </div>
              <div className="pos-pm-quick-keys">
                <button className="pos-pm-qk pos-pm-qk--discount" onClick={() => setAmount(due.toFixed(2))}>Discount</button>
                <button className="pos-pm-qk pos-pm-qk--total" onClick={() => setAmount(safeTotal.toFixed(2))}>{safeTotal.toFixed(2)}</button>
                {[1, 5, 10, 20, 50, 100].map(v => (
                  <button key={v} className="pos-pm-qk" onClick={() => handleQuickKey(v)}>{v}</button>
                ))}
                <button className="pos-pm-qk pos-pm-qk--clear" onClick={handleClear}>Clear</button>
              </div>
            </div>

            {/* Invoice options */}
            <div className="pos-pm-options">
              <label><input type="checkbox" checked={sendSMS} onChange={e => setSendSMS(e.target.checked)} /> Send Invoice Via SMS</label>
              <label><input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} /> Send Invoice Via Email</label>
            </div>
          </div>
        </div>

        <div className="pos-pm-footer">
          <button className="pos-pm-cancel" onClick={onClose}>Cancel</button>
          <button className="pos-pm-submit" onClick={() => {
            if (type === 'split' && totalPaid <= 0) {
              addToast('Please add at least one payment to the list before submitting.', 'error');
              return;
            }
            
            if (type === 'split' && totalPaid < (safeTotal - 0.01)) {
              addToast('Total payment added is less than the Grand Total.', 'error');
              return;
            }

            const finalAmt = totalPaid > 0 ? totalPaid : parseFloat(amount || 0) || safeTotal;
            onConfirm(finalAmt, false, payments);
          }}>Submit</button>
        </div>

      </div>
    </div>
  );
};

/* ── Age Verification Modal ─────────────────────────── */
const AGE_RESTRICTED_KEYWORDS = [
  // Tobacco / Nicotine
  'vape', 'vapes', 'cigarette', 'cigarettes', 'tobacco', 'cigar', 'cigars',
  'nicotine', 'hookah', 'e-cig', 'ecig', 'juul', 'chew', 'dip', 'snuff',
  // Alcohol
  'wine', 'beer', 'alcohol', 'liquor', 'spirits', 'whiskey', 'whisky',
  'vodka', 'rum', 'bourbon', 'ale', 'lager', 'tequila', 'gin', 'brandy',
  'champagne', 'cider', 'malt', 'sake', 'seltzer hard', 'hard seltzer',
  'corona', 'heineken', 'budweiser', 'miller', 'coors',
];

const isAgeRestricted = (productName = '') => {
  const lower = productName.toLowerCase();
  return AGE_RESTRICTED_KEYWORDS.some(kw => lower.includes(kw));
};

const AgeVerificationModal = ({ productName, onVerify, onOverride, onCancel }) => {
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');
  const MIN_AGE = 21;
  const today = dateUtils.getChicagoISOString().split('T')[0];
  const [ty, tm, td] = today.split('-').map(Number);
  const cutoff = new Date(ty - MIN_AGE, tm - 1, td);
  const cutoffStr = `${tm}/${td}/${ty - MIN_AGE}`;

  const handleVerify = () => {
    if (!dob) { setError('Please enter a date of birth.'); return; }
    const entered = new Date(dob);
    if (isNaN(entered.getTime())) { setError('Invalid date. Please use MM/DD/YYYY.'); return; }
    if (entered <= cutoff) {
      onVerify();
    } else {
      setError(`Customer is under ${MIN_AGE}. Cannot complete sale for age-restricted items.`);
    }
  };

  return (
    <div className="pos-modal-overlay" onClick={e => e.stopPropagation()}>
      <div className="pos-modal pos-modal--age" onClick={e => e.stopPropagation()}>
        <div className="pos-age-header">
          <span className="pos-age-icon">🔞</span>
          <h2>Age Verification</h2>
        </div>
        <div className="pos-age-body">
          <div className="pos-age-scan-title">SCAN YOUR ID</div>
          <p className="pos-age-desc">Please enter date of birth BEFORE, to verify your age.</p>
          <div className="pos-age-info-box">
            <div className="pos-age-min">Minimum Age Required: {MIN_AGE}</div>
            <div className="pos-age-cutoff">{cutoffStr}</div>
          </div>
          <input
            type="date"
            className="pos-age-input"
            value={dob}
            onChange={e => { setDob(e.target.value); setError(''); }}
            max={today}
          />
          {error && <div className="pos-age-error">{error}</div>}
        </div>
        <div className="pos-age-footer">
          <button className="pos-age-btn pos-age-btn--override" onClick={onOverride}>Override</button>
          <button className="pos-age-btn pos-age-btn--verify" onClick={handleVerify}>Verify</button>
          <button className="pos-age-btn pos-age-btn--cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── Audio Helper ────────────────────────────────────── */
const playBeep = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    
    // Resume context if suspended (common in some browsers)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    // POS "beep" sound - slightly lower frequency for better audibility
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1000, ctx.currentTime); 
    
    // Increased volume from 0.1 to 0.4
    gain.gain.setValueAtTime(0.4, ctx.currentTime); 
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15); 

    osc.start();
    osc.stop(ctx.currentTime + 0.2); // Slightly longer beep
  } catch (err) {
    console.error('Audio beep error:', err);
  }
};

/* ── Print Helper ────────────────────────────────────── */
const buildReceiptHTML = (sale) => {
  const totalQty = sale.items.reduce((s, i) => s + i.quantity, 0);
  const prevBalance = sale.previousBalance || 0;
  const totalPayable = sale.total + prevBalance;

  const rows = sale.items.map((item, i) => {
    const disc = item.discountType === '%'
      ? (item.price * item.quantity) * ((item.discount || 0) / 100)
      : (item.discount || 0);
    const lineTotal = item.isVoided ? 0 : (item.price * item.quantity - disc);
    // Inline style applied per-cell so it survives print rendering (CSS classes in iframe are unreliable)
    const vs = item.isVoided
      ? 'padding:4px 6px;text-decoration:line-through;color:#cc0000;opacity:0.8;'
      : 'padding:4px 6px;';
    return `
      <tr>
        <td style="${vs}">${i + 1}</td>
        <td style="${vs}">${item.name}${item.isVoided ? ' [VOIDED]' : ''}</td>
        <td style="${vs}">$${item.price.toFixed(2)}</td>
        <td style="${vs}">${item.isVoided ? 0 : item.quantity} Each</td>
        <td style="${vs}">($${(item.isVoided ? 0 : disc).toFixed(2)})</td>
        <td style="${vs}">$${lineTotal.toFixed(2)}</td>
      </tr>`;
  }).join('');

  const totalsRows = [
    prevBalance > 0 ? `<tr><td><strong>Previous Balance</strong></td><td>$${prevBalance.toFixed(2)} (Debit)</td></tr>` : '',
    `<tr><td><strong>Subtotal</strong></td><td>$${sale.subtotal.toFixed(2)}</td></tr>`,
    sale.tax > 0 ? `<tr><td><strong>Tax</strong></td><td>$${sale.tax.toFixed(2)}</td></tr>` : '',
    `<tr><td><strong>Total Payable</strong></td><td>$${totalPayable.toFixed(2)}</td></tr>`,
    `<tr><td><strong>Paid Amount</strong></td><td>$${sale.paid.toFixed(2)}</td></tr>`,
    `<tr><td><strong>Payment Method</strong></td><td></td></tr>`,
    `<tr><td>${sale.paymentMethod}</td><td>$${sale.paid.toFixed(2)}</td></tr>`,
    sale.change > 0 ? `<tr><td>Change</td><td>$${sale.change.toFixed(2)}</td></tr>` : '',
    sale.ageVerified ? `<tr><td><strong>Age Verified</strong></td><td></td></tr>` : '',
  ].join('');

  return `<!DOCTYPE html><html><head><title>Receipt</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 13px; color: #111; padding: 20px; }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .addr { font-size: 12px; color: #333; }
    .title { text-align: center; font-weight: 700; font-size: 15px; margin: 10px 0 8px; text-decoration: underline; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; margin: 8px 0; }
    th { font-weight: 700; text-align: left; padding: 5px 6px; border-top: 1px dashed #999; border-bottom: 1px dashed #999; white-space: nowrap; }
    td { padding: 4px 6px; vertical-align: top; }
    tbody tr:last-child td { border-bottom: 1px dashed #ccc; }
    .tfoot td { border-top: 1px dashed #999; padding: 5px 6px; }
    .totals { margin-top: 10px; font-size: 12px; width: 100%; border-collapse: collapse; }
    .totals td { padding: 2px 0; border-bottom: 1px dashed #e5e5e5; }
    .totals tr:last-child td { border-bottom: none; }
    .totals td:last-child { text-align: right; }
    .footer { text-align: center; margin-top: 14px; font-size: 12px; font-weight: 600; border-top: 1px dashed #aaa; padding-top: 8px; }
    .voided-line td { text-decoration: line-through; color: #ef4444; opacity: 0.75; }
    .void-watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 70px;
      color: rgba(239, 68, 68, 0.18);
      font-weight: 700;
      border: 6px solid rgba(239, 68, 68, 0.18);
      padding: 10px 30px;
      white-space: nowrap;
      z-index: 1000;
      pointer-events: none;
      font-family: Arial, sans-serif;
    }
    .void-box {
      border: 3px solid #ef4444;
      color: #ef4444;
      padding: 5px 10px;
      font-size: 20px;
      display: inline-block;
      margin-top: 10px;
      transform: rotate(-5deg);
    }
  </style>
  </head><body>
    ${sale.isVoided ? '<div class="void-watermark">VOIDED</div>' : ''}
    <div class="center bold" style="font-size:16px">${sale.location || 'Lefty\'s Liquor'}</div>
    <div class="center" style="font-size:13px;font-weight:600">${sale.location || ''}</div>
    ${sale.address ? `<div class="center addr">${sale.address}</div>` : ''}
    ${sale.email ? `<div class="center addr">Email: ${sale.email}</div>` : ''}
    ${sale.phone ? `<div class="center addr">Phone: ${sale.phone}</div>` : ''}
    <div class="title">${sale.isVoided ? 'Void Receipt' : 'Sales Receipt'}</div>
    ${sale.isVoided ? '<div class="center"><div class="void-box">VOIDED</div></div>' : ''}
    <div class="meta">
      <div>
        <div><strong>Bill To:</strong> ${sale.customer}</div>
        <div><strong>Cashier:</strong> ${sale.cashier || 'encore'}</div>
        <div><strong>Counter:</strong> ${sale.counter || 'POS'}</div>
      </div>
      <div style="text-align:right">
        <div><strong>Invoice No:</strong> ${sale.invoiceNo}</div>
        <div><strong>Date:</strong> ${sale.date}</div>
        <div><strong>Time:</strong> ${sale.time || ''}</div>
      </div>
    </div>
    <table>
      <thead><tr><th>SN</th><th>Item</th><th>Price</th><th>Qty</th><th>Discount</th><th>Total</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr class="tfoot">
        <td colspan="3"></td>
        <td><strong>Total ${totalQty} (${sale.items.length})</strong></td>
        <td><strong>$0.00</strong></td>
        <td><strong>$${(sale.subtotal + (sale.tax || 0)).toFixed(2)}</strong></td>
      </tr></tfoot>
    </table>
    <table class="totals"><tbody>${totalsRows}</tbody></table>
    <div class="footer">Thank you for your business!</div>
  </body></html>`;
};

const printReceipt = (sale) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(buildReceiptHTML(sale));
  doc.close();
  iframe.contentWindow.focus();
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 300);
};

/* ── Receipt Preview Modal ──────────────────────────── */
const ReceiptModal = ({ sale, onClose, autoPrint }) => {
  useEffect(() => {
    if (autoPrint) {
      const timer = setTimeout(() => printReceipt(sale), 400);
      return () => clearTimeout(timer);
    }
  }, [autoPrint]);

  const totalQty = sale.items.reduce((s, i) => s + i.quantity, 0);
  const prevBalance = sale.previousBalance || 0;
  const totalPayable = sale.total + prevBalance;

  return (
    <div className="pos-modal-overlay" onClick={onClose}>
      <div className="pos-modal pos-modal--receipt" onClick={e => e.stopPropagation()}>
        <div className="pos-modal-header">
          <h2>🧾 Receipt Preview</h2>
          <button className="pos-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="pos-receipt">
          {/* Store header */}
          <div className="pos-receipt-store-name">{sale.location || ''}</div>
          <div className="pos-receipt-store-sub">{sale.location || ''}</div>
          {sale.address && <div className="pos-receipt-store-addr">{sale.address}</div>}
          {sale.email && <div className="pos-receipt-store-addr">Email: {sale.email}</div>}
          {sale.phone && <div className="pos-receipt-store-addr">Phone: {sale.phone}</div>}

          <div className="pos-receipt-title">Sales Receipt</div>

          {/* Meta row */}
          <div className="pos-receipt-meta">
            <div>
              <div><strong>Bill To:</strong> {sale.customer}</div>
              <div><strong>Cashier:</strong> {sale.cashier || 'encore'}</div>
              <div><strong>Counter:</strong> {sale.counter || 'POS'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div><strong>Invoice No:</strong> {sale.invoiceNo}</div>
              <div><strong>Date:</strong> {sale.date}</div>
              <div><strong>Time:</strong> {sale.time || dateUtils.formatTime(new Date())}</div>
            </div>
          </div>

          {/* Items table */}
          <table className="pos-receipt-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Item</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Discount</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sale.items.map((item, i) => {
                const disc = item.discountType === '%'
                  ? (item.price * item.quantity) * ((item.discount || 0) / 100)
                  : (item.discount || 0);
                const lineTotal = item.isVoided ? 0 : (item.price * item.quantity - disc);
                return (
                  <tr key={i} className={item.isVoided ? 'pos-receipt-voided-line' : ''}>
                    <td>{i + 1}</td>
                    <td>{item.name}</td>
                    <td>${item.price.toFixed(2)}</td>
                    <td>{item.isVoided ? 0 : item.quantity} Each</td>
                    <td>($${(item.isVoided ? 0 : disc).toFixed(2)})</td>
                    <td>${lineTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="pos-receipt-tfoot">
                <td colSpan={3}></td>
                <td><strong>Total {totalQty} ({sale.items.length})</strong></td>
                <td><strong>$0.00</strong></td>
                <td><strong>${sale.subtotal.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>

          {/* Totals */}
          <div className="pos-receipt-totals">
            {prevBalance > 0 && (
              <div><span><strong>Previous Balance</strong></span><span>${prevBalance.toFixed(2)} (Debit)</span></div>
            )}
            <div><span><strong>Subtotal</strong></span><span>$${sale.subtotal.toFixed(2)}</span></div>
            {sale.tax > 0 && <div><span><strong>Tax</strong></span><span>$${sale.tax.toFixed(2)}</span></div>}
            <div><span><strong>Total Payable</strong></span><span>$${totalPayable.toFixed(2)}</span></div>
            <div><span><strong>Paid Amount</strong></span><span>${sale.paid.toFixed(2)}</span></div>
            <div><span><strong>Payment Method</strong></span><span></span></div>
            <div><span>{sale.paymentMethod}</span><span>${sale.paid.toFixed(2)}</span></div>
            {sale.change > 0 && <div><span>Change</span><span>${sale.change.toFixed(2)}</span></div>}
            {sale.ageVerified && <div><span><strong>Age Verified</strong></span><span></span></div>}
          </div>
          <div className="pos-receipt-footer">Thank you for your business!</div>
        </div>
        <div className="pos-modal-footer">
          <button className="pos-modal-btn pos-modal-btn--cancel" onClick={onClose}>Close</button>
          <button className="pos-modal-btn pos-modal-btn--confirm" onClick={() => printReceipt(sale)}>🖨 Print</button>
        </div>
      </div>
    </div>
  );
};

/* ── Main POS Component ─────────────────────────────── */
const POSPage = () => {
  /* Contexts & Roles */
  const { hasPermission, userRole } = usePermissionContext();
  const roleName = userRole?.name?.toLowerCase() || '';
  const isSuperAdmin = roleName === 'superadmin' || roleName === 'super admin' || roleName === 'super_admin';

  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState({ id: 0, name: 'Walk-in Customer' });
  const [businessLocations, setBusinessLocations] = useState([]);
  const [location, setLocation] = useState('');
  const [locationDetails, setLocationDetails] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [toasts, setToasts] = useState([]);
  const [heldSales, setHeldSales] = useState([]);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [autoPrintReceipt, setAutoPrintReceipt] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showOpenSalesModal, setShowOpenSalesModal] = useState(false);
  const [showRegisterDetailsModal, setShowRegisterDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('cash');
  const [activeTab, setActiveTab] = useState('all');
  const [userData, setUserData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showHeldSalesPanel, setShowHeldSalesPanel] = useState(false);
  const [showCustomerPanel, setShowCustomerPanel] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [orderTax] = useState(0);
  const [charge, setCharge] = useState(0);
  // Rounding is now auto-calculated based on decimal: >= 0.50 rounds up, < 0.50 rounds down
  const [commissionAgent, setCommissionAgent] = useState('');
  const [deliveryPartner, setDeliveryPartner] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceCounter, setInvoiceCounter] = useState(1001);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [customerForEdit, setCustomerForEdit] = useState(null);
  const [isFetchingCustomer, setIsFetchingCustomer] = useState(false);
  const [showPriceLookupModal, setShowPriceLookupModal] = useState(false);
  const [priceLookupQuery, setPriceLookupQuery] = useState('');
  const [priceLookupResults, setPriceLookupResults] = useState([]);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  // Age verification
  const [ageVerifyProduct, setAgeVerifyProduct] = useState(null); // product pending age check
  const [ageVerified, setAgeVerified] = useState(false); // did current sale have age verification
  // Tax definitions
  const [taxRates, setTaxRates] = useState([]);
  const [isLoadingTaxRates, setIsLoadingTaxRates] = useState(false);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  /* Toast helpers */
  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  const removeToast = useCallback(id => setToasts(prev => prev.filter(t => t.id !== id)), []);

  /* Date/time */
  const updateDateTime = useCallback(() => {
    setCurrentDateTime(dateUtils.formatDateTime(new Date()));
  }, []);

  /* Load data */
  const loadProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('POS: Loading products...');
      const data = await apiService.getProducts({ per_page: 250 });
      console.log('POS: Products response:', data);
      const list = data.data || data || [];
      console.log('POS: Extracted product list:', list.length, 'products');
      setProducts(list);
      setFilteredProducts(list);
      // Extract unique categories
      const cats = ['all', ...new Set(list.map(p => p.category?.name || p.category || '').filter(Boolean))];
      setCategories(cats);
      if (list.length > 0) {
        // addToast(`Loaded ${list.length} products`, 'success');
      } else {
        addToast('No products found. Add products first.', 'info');

      }
    } catch (err) {
      console.error('POS: Error loading products:', err);
      addToast(`Failed to load products: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  const loadCustomers = useCallback(async () => {
    try {
      const data = await apiService.getContacts();
      const list = (data.data || data || []).map(c => ({
        id: c.id,
        name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        phone: c.mobile || c.contact_no || '',
        email: c.email || ''
      }));
      if (!list.find(c => c.name === 'Walk-in Customer')) {
        list.unshift({ id: 0, name: 'Walk-in Customer', phone: '', email: '' });
      }
      setCustomers(list);
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([{ id: 0, name: 'Walk-in Customer', phone: '', email: '' }]);
    }
  }, []);

  const loadBusinessLocations = useCallback(async () => {
    try {
      const data = await apiService.getBusinessLocations();
      const list = data.data || data || [];
      setBusinessLocations(list);
      if (list.length > 0) {
        setLocation(list[0].name || '');
        setLocationDetails(list[0]);
      }
    } catch (err) { console.error('Error loading locations:', err); }
  }, []);

  const loadUserData = useCallback(async () => {
    try {
      const data = await apiService.getUserDetails();
      setUserData(data.data || data);
    } catch (err) { console.error('Error loading user data:', err); }
  }, []);

  const loadTaxRates = useCallback(async () => {
    try {
      setIsLoadingTaxRates(true);
      const data = await apiService.getTaxRates();
      const list = (data.data || data || []).map(t => ({
        id: t.id,
        name: t.name || t.tax_name || `Tax ${t.id}`,
        amount: parseFloat(t.amount || t.rate || 0),
        type: t.type || 'percentage'
      }));
      setTaxRates(list);
    } catch (err) {
      console.error('Error loading tax rates:', err);
    } finally {
      setIsLoadingTaxRates(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadCustomers();
    loadBusinessLocations();
    loadUserData();
    loadTaxRates();
    updateDateTime();
    const interval = setInterval(updateDateTime, 30000);
    return () => clearInterval(interval);
  }, [loadProducts, loadCustomers, loadBusinessLocations, loadUserData, loadTaxRates, updateDateTime]);

  /* Filter products */
  useEffect(() => {
    let filtered = products;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p =>
        (p.category?.name || p.category || '') === selectedCategory
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        (p.name || p.product || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q)
      );
    }
    setFilteredProducts(filtered);
  }, [searchTerm, selectedCategory, products]);

  /* Cart helpers */
  const calculateProductTax = useCallback((unitPrice, taxName, taxType = 'exclusive') => {
    if (!taxName || taxName === 'None' || taxName === 'No Tax' || taxName === 'NoTax') {
      return { taxAmount: 0, basePrice: unitPrice };
    }

    const normalizedName = String(taxName).trim().toLowerCase();
    const selectedTax = taxRates.find(tax => {
      const rateName = String(tax.name || '').trim().toLowerCase();
      const rateId = String(tax.id || '').trim().toLowerCase();
      return rateName === normalizedName || rateId === normalizedName;
    });

    if (selectedTax && selectedTax.amount > 0) {
      const rate = selectedTax.amount;
      if (selectedTax.type === 'percentage') {
        if (taxType === 'inclusive') {
          const taxAmount = unitPrice - (unitPrice / (1 + rate / 100));
          return { taxAmount, basePrice: unitPrice - taxAmount };
        } else {
          const taxAmount = (unitPrice * rate) / 100;
          return { taxAmount, basePrice: unitPrice };
        }
      } else if (selectedTax.type === 'fixed') {
        const taxAmount = selectedTax.amount;
        if (taxType === 'inclusive') {
          return { taxAmount, basePrice: Math.max(0, unitPrice - taxAmount) };
        } else {
          return { taxAmount, basePrice: unitPrice };
        }
      }
    }
    return { taxAmount: 0, basePrice: unitPrice };
  }, [taxRates]);

  const getPrice = p => parseFloat(
    p.unit_price || p.price || p.sell_price_inc_tax ||
    p.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax || 0
  );

  const addToCartDirect = useCallback((product) => {
    const unitPrice = getPrice(product);

    // Dynamic tax field detection
    const taxName = product.tax?.name || product.tax_name || product.tax || product.applicable_tax || product.tax_id || 'None';
    const taxType = (product.tax_type || product.selling_price_tax_type || 'exclusive').toLowerCase();

    const { taxAmount, basePrice } = calculateProductTax(unitPrice, taxName, taxType);

    addToast(`${product.name || product.product} added to cart`, 'success');
    playBeep();
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      // Get variation_id from product
      const variationId = (product.product_variations?.[0]?.variations?.[0]?.id) || product.id;

      return [...prev, {
        id: product.id,
        variation_id: variationId, // Store variation_id for API
        name: product.name || product.product || 'Unknown',
        sku: product.sku || '',
        price: basePrice, // Store exclusive price
        unitTax: taxAmount,
        taxName,
        taxType,
        quantity: 1,
        discount: 0,
        discountType: '$',
        isVoided: false
      }];
    });
    setTimeout(() => searchRef.current?.focus(), 50);
  }, [addToast, calculateProductTax]);

  const addToCart = useCallback((product) => {
    const name = product.name || product.product || '';
    if (isAgeRestricted(name)) {
      // pause and ask for age verification
      setAgeVerifyProduct(product);
      return;
    }
    addToCartDirect(product);
  }, [addToCartDirect]);

  const updateQuantity = useCallback((id, qty) => {
    const q = parseInt(qty);
    if (q <= 0) {
      setCart(prev => prev.filter(i => i.id !== id));
      addToast('Item removed', 'info');
    } else {
      setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: q } : i));
    }
  }, [addToast]);

  const updateDiscount = useCallback((id, disc) => {
    setCart(prev => prev.map(i => i.id === id ? { ...i, discount: parseFloat(disc) || 0 } : i));
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(i => i.id !== id));
    addToast('Item removed', 'info');
  }, [addToast]);

  /* Calculations */
  const subtotal = cart.reduce((s, i) => {
    if (i.isVoided) return s;
    const itemTotal = (i.price * i.quantity);
    const disc = i.discountType === '%'
      ? itemTotal * ((i.discount || 0) / 100)
      : (i.discount || 0);
    return s + (itemTotal - disc);
  }, 0);

  const productTaxAmount = cart.reduce((s, i) => {
    if (i.isVoided) return s;
    return s + ((i.unitTax || 0) * i.quantity);
  }, 0);

  const taxAmount = productTaxAmount + (subtotal * (orderTax / 100));
  const discountAmount = typeof discount === 'number' ? discount : 0;

  // Calculate raw total before rounding
  const rawTotal = Math.max(0, subtotal + taxAmount - discountAmount + charge);

  // Auto-calculate rounding: >= 0.50 rounds up, < 0.50 rounds down
  const autoRounding = (() => {
    const decimal = rawTotal % 1;
    if (decimal >= 0.50) {
      return Math.ceil(rawTotal) - rawTotal; // Positive rounding to round up
    } else {
      return Math.floor(rawTotal) - rawTotal; // Negative rounding to round down
    }
  })();

  const grandTotal = Math.max(0, rawTotal + autoRounding);
  const itemCount = cart.reduce((s, i) => i.isVoided ? s : (s + i.quantity), 0);

  /* Keyboard shortcuts */
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'F1') { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === 'F2') { e.preventDefault(); handleQuickCashPay(); }
      if (e.key === 'F3') { e.preventDefault(); handlePayment('card'); }
      if (e.key === 'Delete') { e.preventDefault(); if (selectedItem) removeFromCart(selectedItem); }
      if (e.key === 'Escape') { setShowPaymentModal(false); setShowHeldSalesPanel(false); }
      if (e.key === 'F4') { e.preventDefault(); handleHold(); }
      if (e.key === 'F5') { e.preventDefault(); handleVoid(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedItem]);  // eslint-disable-line

  /* Barcode scanner simulation */
  useEffect(() => {
    let buffer = '';
    let timer = null;
    const handleBarcode = (e) => {
      if (e.target.tagName === 'INPUT' && e.target !== searchRef.current) return;
      buffer += e.key;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (buffer.length > 4) {
          const match = products.find(p =>
            (p.sku || '').toLowerCase() === buffer.toLowerCase() ||
            (p.barcode || '').toLowerCase() === buffer.toLowerCase()
          );
          if (match) {
            addToCart(match);
            setSearchTerm('');
          }
        }
        buffer = '';
      }, 300);
    };
    window.addEventListener('keypress', handleBarcode);
    return () => { window.removeEventListener('keypress', handleBarcode); clearTimeout(timer); };
  }, [products, addToCart]);

  /* Actions */
  const handleEditCustomer = async () => {
    if (selectedCustomer.id === 0) {
      addToast('Cannot edit Walk-in Customer', 'info');
      return;
    }

    try {
      setIsFetchingCustomer(true);
      const fullCustomer = await apiService.getCustomerById(selectedCustomer.id);
      setCustomerForEdit(fullCustomer);
      setShowEditCustomerModal(true);
    } catch (err) {
      console.error('Error fetching customer for edit:', err);
      addToast('Failed to fetch customer details', 'error');
    } finally {
      setIsFetchingCustomer(false);
    }
  };

  const handlePayment = (type) => {
    if (cart.length === 0) { addToast('Cart is empty', 'error'); return; }
    setPaymentType(type);
    setShowPaymentModal(true);
  };

  const saveSaleToApi = async (amountPaid, pMethod, pList = []) => {
    try {
      const sellData = {
        contact_id: selectedCustomer.id || (customers.find(c => c.id !== 0)?.id || 1),
        location_id: locationDetails?.id || null,
        status: 'final',
        sale_date: dateUtils.formatDateForApi(new Date()),
        final_total: grandTotal,
        discount_amount: discount || 0,
        discount_type: 'fixed',
        tax_id: null,
        tax_amount: taxAmount || 0,
        shipping_charges: charge ? parseFloat(charge).toFixed(4) : "0.0000",
        products: cart.map(item => ({
          product_id: parseInt(item.id),
          variation_id: parseInt(item.variation_id || item.id),
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.price),
          tax_id: null,
          discount_type: item.discountType === '%' ? 'percentage' : 'fixed',
          discount_amount: parseFloat(item.discount || 0)
        })),
        payments: pList.length > 0 ? pList.map(p => ({
          payment_amount: parseFloat(p.amount),
          payment_method: (p.method || 'cash').toLowerCase(),
          paid_on: dateUtils.formatDateForApi(new Date())
        })) : [{
          payment_amount: parseFloat(amountPaid),
          payment_method: (pMethod || 'cash').toLowerCase(),
          paid_on: dateUtils.formatDateForApi(new Date())
        }]
      };

      console.log('--- POS RAW PAYLOAD ---', JSON.stringify(sellData, null, 2));
      // alert('POS SUBMITTING PAYLOAD: ' + JSON.stringify(sellData).substring(0, 300) + '...');

      const response = await apiService.saveSell(sellData);
      console.log('POS Sale saved successfully:', response);
      
      const saleId = response?.id || response?.data?.id || response?.[0]?.id;
      // alert(`POS SALE SAVED! ID: ${saleId || 'Success'}`);
      
      return response;
    } catch (error) {
      console.error('Error saving POS sale:', error);
      alert('POS SAVE ERROR: ' + error.message);
      addToast(`Failed to save sale: ${error.message}`, 'error');
      return null;
    }
  };

  const confirmPayment = useCallback(async (amountPaid, shouldAutoPrint = false, paymentList = []) => {
    // Save to API first
    const pMethod = paymentType === 'card' ? 'Credit Card' : paymentType === 'split' ? 'Split' : 'Cash';
    await saveSaleToApi(amountPaid, pMethod, paymentList);

    const inv = `INV-${invoiceCounter.toString().padStart(5, '0')}`;
    const now = new Date();
    const storeAddress = locationDetails
      ? [locationDetails.address_line_1, locationDetails.city, locationDetails.state, locationDetails.zip_code].filter(Boolean).join(', ')
      : '';
    const receipt = {
      invoiceNo: inv,
      date: dateUtils.formatDate(now),
      time: dateUtils.formatTime(now),
      location,
      address: storeAddress,
      email: locationDetails?.email || locationDetails?.business_email || '',
      phone: locationDetails?.mobile || locationDetails?.phone || locationDetails?.contact_no || '',
      customer: selectedCustomer.name,
      cashier: 'encore',
      counter: 'POS',
      items: cart,
      subtotal,
      tax: taxAmount,
      total: grandTotal,
      paid: amountPaid,
      change: Math.max(0, amountPaid - grandTotal),
      paymentMethod: paymentType === 'card' ? 'Credit Card' : paymentType === 'split' ? 'Split' : 'Cash',
      ageVerified,
    };
    setLastReceipt(receipt);
    setInvoiceCounter(prev => prev + 1);
    setCart([]);
    setDiscount(0);
    setSelectedItem(null);
    setAgeVerified(false);
    setShowPaymentModal(false);
    setAutoPrintReceipt(shouldAutoPrint);
    setShowReceipt(true);
    addToast(`Payment of $${amountPaid.toFixed(2)} received ✓`, 'success');
  }, [cart, subtotal, taxAmount, grandTotal, paymentType, selectedCustomer, location, currentDateTime, invoiceCounter, ageVerified, addToast]);

  /* Quick Cash Pay — exact amount, no modal, auto-print receipt */
  const handleQuickCashPay = useCallback(async () => {
    if (cart.length === 0) { addToast('Cart is empty', 'error'); return; }
    
    // Save to API
    await saveSaleToApi(grandTotal, 'Cash');

    setPaymentType('cash');
    const inv = `INV-${invoiceCounter.toString().padStart(5, '0')}`;
    const now = new Date();
    const storeAddress = locationDetails
      ? [locationDetails.address_line_1, locationDetails.city, locationDetails.state, locationDetails.zip_code].filter(Boolean).join(', ')
      : '';
    const receipt = {
      invoiceNo: inv,
      date: dateUtils.formatDate(now),
      time: dateUtils.formatTime(now),
      location,
      address: storeAddress,
      email: locationDetails?.email || locationDetails?.business_email || '',
      phone: locationDetails?.mobile || locationDetails?.phone || locationDetails?.contact_no || '',
      customer: selectedCustomer.name,
      cashier: 'encore',
      counter: 'POS',
      items: [...cart],
      subtotal,
      tax: taxAmount,
      total: grandTotal,
      paid: grandTotal,
      change: 0,
      paymentMethod: 'Cash',
      ageVerified,
    };
    setLastReceipt(receipt);
    setInvoiceCounter(prev => prev + 1);
    setCart([]);
    setDiscount(0);
    setSelectedItem(null);
    setAgeVerified(false);
    setAutoPrintReceipt(true);
    setShowReceipt(true);
    addToast(`Quick Cash: $${grandTotal.toFixed(2)} paid ✓`, 'success');
  }, [cart, subtotal, taxAmount, grandTotal, selectedCustomer, location, currentDateTime, invoiceCounter, ageVerified, addToast]);

  const handleHold = useCallback(() => {
    if (cart.length === 0) { addToast('Nothing to hold', 'error'); return; }
    const held = { id: Date.now(), customer: selectedCustomer.name, cart: [...cart], total: grandTotal, time: dateUtils.formatTime(new Date()) };
    setHeldSales(prev => [...prev, held]);
    setCart([]);
    setDiscount(0);
    addToast('Sale put on hold', 'info');
  }, [cart, selectedCustomer, grandTotal, addToast]);

  const restoreHeld = useCallback((held) => {
    if (cart.length > 0) {
      if (!window.confirm('Replace current cart with held sale?')) return;
    }
    setCart(held.cart);
    setHeldSales(prev => prev.filter(h => h.id !== held.id));
    setShowHeldSalesPanel(false);
    addToast('Sale restored', 'success');
  }, [cart, addToast]);

  const speak = (text) => {
    try {
      const msg = new SpeechSynthesisUtterance(text);
      msg.rate = 1;
      window.speechSynthesis.speak(msg);
    } catch (e) {}
  };

  const handleVoid = useCallback(() => {
    if (cart.length === 0) { addToast('Cart is already empty', 'info'); return; }
    if (window.confirm('Void the current sale?')) {
      // Print Void Receipt
      const voidSaleData = {
        invoiceNo: 'VOID-' + Date.now(),
        date: dateUtils.getChicagoISOString().split('T')[0],
        time: dateUtils.formatTime(new Date()),
        customer: 'Walk-in Customer',
        location: 'Lefty\'s Liquor',
        items: cart.map(i => ({
          name: i.name || 'Unknown',
          quantity: i.quantity,
          price: i.price,
          isVoided: true
        })),
        subtotal: 0,
        tax: 0,
        paid: 0,
        total: 0,
        change: 0,
        paymentMethod: 'VOIDED',
        isVoided: true
      };
      printReceipt(voidSaleData);

      setCart([]);
      setDiscount(0);
      setSelectedItem(null);
      addToast('Sale voided', 'error');
      speak('Sale Voided');
    }
  }, [cart, addToast]);

  const handleVoidLine = useCallback(() => {
    if (!selectedItem) { addToast('Select an item to void', 'info'); return; }
    const itemToVoid = cart.find(i => i.id === selectedItem);
    
    if (itemToVoid) {
      // Print single item void receipt
      const voidItemData = {
        invoiceNo: 'VOID-ITEM-' + Date.now(),
        date: dateUtils.getChicagoISOString().split('T')[0],
        time: dateUtils.formatTime(new Date()),
        customer: 'Walk-in Customer',
        location: 'Lefty\'s Liquor',
        items: [{
          name: itemToVoid.name,
          quantity: itemToVoid.quantity,
          price: itemToVoid.price,
          isVoided: true
        }],
        subtotal: 0,
        tax: 0,
        paid: 0,
        total: 0,
        change: 0,
        paymentMethod: 'VOIDED',
        isVoided: true
      };
      printReceipt(voidItemData);
    }

    setCart(prev => prev.map(i => i.id === selectedItem ? { ...i, isVoided: !i.isVoided } : i));
    setSelectedItem(null);
    speak('Item Voided');
  }, [selectedItem, cart, addToast]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => { });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  /* ── Render ─────────────────────────────────────────── */
  return (
    <div className="pos-wrap">
      {/* Toast */}
      <Toast toasts={toasts} removeToast={removeToast} />

      {/* ── TOP TOOLBAR ── */}
      <header className="pos-toolbar">
        <div className="pos-toolbar-brand">
          <span className="pos-toolbar-logo">ET<span>POS</span></span>
          <select
            className="pos-toolbar-location"
            value={location}
            onChange={e => {
              setLocation(e.target.value);
              const found = businessLocations.find(l => l.name === e.target.value);
              if (found) setLocationDetails(found);
            }}
          >
            {businessLocations.length > 0
              ? businessLocations.map(l => <option key={l.id} value={l.name}>{l.name}</option>)
              : <option>{location || 'Select Location'}</option>}
          </select>
        </div>

        <nav className="pos-toolbar-nav">
          <button
            className={`pos-tb-btn ${heldSales.length > 0 ? 'pos-tb-btn--badge' : ''}`}
            onClick={() => setShowHeldSalesPanel(true)}
            title="Hold Sales (F4)"
          >
            <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            <span>Hold Sales</span>
            {heldSales.length > 0 && <em className="pos-badge">{heldSales.length}</em>}
          </button>
          <button className="pos-tb-btn" onClick={() => lastReceipt ? setShowReceipt(true) : addToast('No receipt yet', 'info')} title="Last Receipt">
            <svg viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
            <span>Last Receipt</span>
          </button>
          <button className="pos-tb-btn" onClick={() => setShowOpenSalesModal(true)} title="Open Sales">
            <svg viewBox="0 0 24 24"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
            <span>Open Sales</span>
          </button>
          <button className="pos-tb-btn pos-tb-btn--warn" onClick={() => setShowRegisterDetailsModal(true)} title="Close Register">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
            <span>Close Register</span>
          </button>
          <button className="pos-tb-btn" onClick={() => setShowCustomerPanel(p => !p)} title="Customer Panel">
            <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            <span>Customer Panel</span>
          </button>
          <button className="pos-tb-btn" onClick={toggleFullscreen} title="Fullscreen">
            {isFullscreen
              ? <svg viewBox="0 0 24 24"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" /></svg>
              : <svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>}
            <span>{isFullscreen ? 'Exit FS' : 'Fullscreen'}</span>
          </button>
          <button className="pos-tb-btn" onClick={() => setShowSidebar(p => !p)} title="Toggle Sidebar">
            <svg viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            <span>Side Menu</span>
          </button>
        </nav>

        <div className="pos-toolbar-info">
          <div className="pos-clock">{currentDateTime}</div>
        </div>
      </header>

      {/* ══ SLIDING ENTIRE UI SIDEBAR OVERLAY ══ */}
      {showSidebar && (
        <div className="pos-sidebar-overlay" onClick={() => setShowSidebar(false)} />
      )}
      <div className={`pos-sidebar-container ${showSidebar ? 'open' : ''}`}>
        <div className="pos-sidebar-header" style={{ justifyContent: 'flex-end' }}>
          <button className="pos-sidebar-close" onClick={() => setShowSidebar(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="pos-sidebar-menu" style={{ paddingBottom: '40px' }}>
          {[
            {
              title: 'Main',
              items: [
                { id: 'system-settings', label: 'Superadmin', icon: '👑', permission: 'superadmin.access' },
                { id: 'home', label: 'Home', icon: '🏠', permission: null },
                { id: 'users', label: 'User Management', icon: '👥', permission: 'user.view' },
                { id: 'contacts', label: 'Contacts', icon: '📇', permission: 'contact.view' },
                { id: 'sell', label: 'Sell', icon: '💰', permission: 'sell.view' },
                { id: 'products', label: 'Products', icon: '📦', permission: 'product.view' },
                { id: 'purchases', label: 'Purchases', icon: '🛒', permission: 'purchase.view' },
              ]
            },
            {
              title: 'Transactions',
              items: [
                { id: 'stock-transfers', label: 'Stock Transfers', icon: '🔄', permission: 'stock_transfer.view' },
                { id: 'stock-adjustment', label: 'Stock Adjustment', icon: '⚖️', permission: 'stock_adjustment.view' },
              ]
            },
            {
              title: 'Financial',
              items: [
                { id: 'expenses', label: 'Expenses', icon: '💸', permission: 'expense.view' },
                { id: 'payment-accounts', label: 'Payment Accounts', icon: '💳', permission: 'payment_account.view' },
                { id: 'accounting', label: 'Accounting', icon: '📊', permission: 'accounting.view' },
                { id: 'reports', label: 'Reports', icon: '📈', permission: 'report.view' },
              ]
            },
            {
              title: 'System',
              items: [
                { id: 'notification-templates', label: 'Notification Templates', icon: '📧', permission: 'notification.view' },
                { id: 'settings', label: 'Settings', icon: '⚙️', permission: 'settings.view' },
                { id: 'crm', label: 'CRM', icon: '🤝', permission: 'crm.view' },
                { id: 'woocommerce', label: 'Woocommerce Connector', icon: '🌐', permission: 'woocommerce.view' },
              ]
            }
          ].map((cat, cIdx) => {
            let filteredItems = cat.items.filter(item => !item.permission || hasPermission(item.permission));

            // Explicitly verify the User is SuperAdmin before showing System Settings
            if (!isSuperAdmin) {
              filteredItems = filteredItems.filter(item => item.id !== 'system-settings');
            }

            if (filteredItems.length === 0) return null;

            return (
              <div key={cIdx} className="menu-category" style={{ marginBottom: '1.5rem', padding: '0 10px' }}>
                <div className="category-title" style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', marginBottom: '0.5rem' }}>
                  {cat.title}
                  {cat.title === 'Main' && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: '600', color: '#f59e0b' }}>V6.4</span>}
                </div>
                {filteredItems.map((item, iIdx) => (
                  <div
                    key={iIdx}
                    onClick={() => {
                      setShowSidebar(false);
                      navigate(`/${item.id}`);
                    }}
                    className="nav-item pos-sidebar-link"
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0.75rem 1rem', gap: '0.75rem', color: '#1e293b', fontSize: '0.875rem', fontWeight: '500', transition: 'all 0.2s ease', borderRadius: '0.375rem', margin: '0 10px', width: 'auto' }}
                  >
                    <span className="nav-icon pos-sidebar-icon" style={{ fontSize: '1rem', minWidth: '20px', textAlign: 'center', marginRight: '0' }}>{item.icon}</span>
                    <span className="nav-label pos-sidebar-text">{item.label}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div className="pos-sidebar-footer">
          <button className="pos-sidebar-toggle-btn" onClick={() => setShowSidebar(false)}>
            ◀ Collapse Sidebar
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <main className="pos-main">

        {/* ══ LEFT PANEL — Cart ══ */}
        <section className="pos-left">
          {/* Customer + Commission Agent bar */}
          <div className="pos-customer-bar">
            {/* Commission agent / location selector */}
            <select
              className="pos-agent-select"
              value={commissionAgent}
              onChange={e => setCommissionAgent(e.target.value)}
              title="Commission Agent"
            >
              <option value="">Lefty Leqouer</option>
              <option value="agent1">Agent 1</option>
              <option value="agent2">Agent 2</option>
            </select>

            {/* Customer selector */}
            <select
              className="pos-customer-select"
              value={selectedCustomer.id}
              onChange={e => {
                const c = customers.find(c => c.id === parseInt(e.target.value)) || customers[0];
                setSelectedCustomer(c);
              }}
            >
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {/* Pencil / edit icon */}
            <button className="pos-icon-btn" title="Edit Customer" onClick={handleEditCustomer}>
              <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            </button>

            {/* Add Customer / (+) icon inside a circle */}
            <button className="pos-icon-btn" title="Add Customer" onClick={() => setShowAddCustomerModal(true)}>
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
            </button>
          </div>

          {/* Cart table */}
          <div className="pos-cart-area">
            {cart.length === 0 ? (
              <div className="pos-cart-empty">
                <svg viewBox="0 0 24 24" width="72" height="72">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                <p>Cart is empty</p>
                <p className="pos-cart-empty-sub">Search or click a product to add</p>
              </div>
            ) : (
              <table className="pos-cart-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Price</th>
                    <th>Qty</th>
                    <th>Discount</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map(item => {
                    const priceToDisplay = (item.price || 0) + (item.unitTax || 0);
                    const disc = item.discountType === '%'
                      ? (priceToDisplay * item.quantity) * ((item.discount || 0) / 100)
                      : (item.discount || 0);
                    const lineTotal = item.isVoided ? 0 : (priceToDisplay * item.quantity - disc);
                    const isSelected = selectedItem === item.id;
                    return (
                      <tr
                        key={item.id}
                        className={`pos-cart-row ${isSelected ? 'pos-cart-row--selected' : ''} ${item.isVoided ? 'pos-cart-row--voided' : ''}`}
                        onClick={() => setSelectedItem(item.id)}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', position: 'relative' }}>
                            {item.isVoided && <span className="pos-voided-badge">VOIDED</span>}
                            <button
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', color: '#1e293b', marginTop: '2px' }}
                              onClick={e => { e.stopPropagation(); setEditingCartItem({ ...item }); }}
                              title="Edit Item"
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                            </button>
                            <div>
                              <div className="pos-cart-item-name">{item.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {item.sku && <div className="pos-cart-item-sku" style={{ marginBottom: 0 }}>{item.sku}</div>}
                                {item.taxType && (
                                  <span className={`pos-tax-badge pos-tax-badge--${item.taxType}`}>
                                    {item.taxType === 'inclusive' ? 'Inc.' : 'Exc.'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="pos-cart-price">${item.price.toFixed(2)}</td>
                        <td>
                          <div className="pos-qty-control">
                            <button
                              className="pos-qty-btn"
                              disabled={item.isVoided}
                              onClick={e => { e.stopPropagation(); updateQuantity(item.id, item.quantity - 1); }}
                            >−</button>
                            <input
                              type="number"
                              className="pos-qty-input"
                              value={item.isVoided ? 0 : item.quantity}
                              readOnly={item.isVoided}
                              onChange={e => updateQuantity(item.id, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              min="1"
                            />
                            <button
                              className="pos-qty-btn"
                              disabled={item.isVoided}
                              onClick={e => { e.stopPropagation(); updateQuantity(item.id, item.quantity + 1); }}
                            >+</button>
                          </div>
                        </td>
                        <td>
                          <div className="pos-disc-cell" onClick={e => e.stopPropagation()}>
                            <input
                              type="number"
                              className="pos-discount-input"
                              value={item.isVoided ? 0 : item.discount}
                              readOnly={item.isVoided}
                              onChange={e => updateDiscount(item.id, e.target.value)}
                              min="0"
                              placeholder="0"
                            />
                            {/* $ / % toggle */}
                            <button
                              className={`pos-disc-type ${item.discountType === '%' ? 'pos-disc-type--pct' : 'pos-disc-type--dollar'}`}
                              disabled={item.isVoided}
                              onClick={e => {
                                e.stopPropagation();
                                setCart(prev => prev.map(i =>
                                  i.id === item.id
                                    ? { ...i, discountType: i.discountType === '%' ? '$' : '%' }
                                    : i
                                ));
                              }}
                              title={`Switch to ${item.discountType === '%' ? 'fixed ($)' : 'percent (%)'}`}
                            >
                              {item.discountType === '%' ? '%' : '$'}
                            </button>
                          </div>
                        </td>
                        <td className="pos-cart-subtotal">${(item.isVoided ? 0 : lineTotal).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Summary footer (matches reference photo) ── */}
          <div className="pos-summary">
            {/* Row 1: 3 action icons | Total | Total Item */}
            <div className="pos-summary-info-row">
              <div className="pos-summary-actions">
                {/* Print receipt icon */}
                <button className="pos-sum-icon-btn" title="Print Receipt" onClick={() => lastReceipt ? setShowReceipt(true) : addToast('No receipt yet', 'info')}>
                  <svg viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="6" rx="1" /><path d="M3 8h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><line x1="7" y1="13" x2="17" y2="13" /><line x1="7" y1="17" x2="11" y2="17" /></svg>
                </button>
                {/* Calendar icon */}
                <button className="pos-sum-icon-btn" title="View Transactions" onClick={() => addToast('Transaction history coming soon', 'info')}>
                  <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                </button>
                {/* Options / cut icon */}
                <button className="pos-sum-icon-btn" title="More Options" onClick={() => setShowCouponModal(true)}>
                  <svg viewBox="0 0 24 24"><circle cx="6" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><line x1="8" y1="8" x2="16" y2="16" /><line x1="6" y1="9" x2="6" y2="21" /><line x1="18" y1="3" x2="18" y2="9" /></svg>
                </button>
              </div>
              <div className="pos-summary-stat">
                <span className="pos-stat-label">Total:</span>
                <span className="pos-stat-value pos-stat-value--accent">{subtotal.toFixed(2)}</span>
              </div>
              <div className="pos-summary-stat">
                <span className="pos-stat-label">Total Item:</span>
                <span className="pos-stat-value pos-stat-value--accent">{cart.reduce((s, i) => s + i.quantity, 0)} ({cart.length})</span>
              </div>
            </div>

            {/* Row 2: Tax | Charge | Discount */}
            <div className="pos-summary-fields-row">
              <div className="pos-summary-field">
                <span className="pos-field-label">
                  <svg viewBox="0 0 24 24" width="12" height="12"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  Tax:
                </span>
                <span className="pos-field-val pos-field-val--muted">{taxAmount.toFixed(2)}</span>
              </div>
              <div className="pos-summary-field">
                <span className="pos-field-label">
                  <svg viewBox="0 0 24 24" width="12" height="12"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                  Charge:
                </span>
                <input
                  type="number"
                  className="pos-field-input"
                  value={charge}
                  onChange={e => setCharge(parseFloat(e.target.value) || 0)}
                  min="0"
                />
              </div>
              <div className="pos-summary-field">
                <span className="pos-field-label">
                  <svg viewBox="0 0 24 24" width="12" height="12"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
                  Discount:
                </span>
                <span className="pos-field-val">
                  <input
                    type="number"
                    className="pos-field-input"
                    value={discount}
                    onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
                    min="0"
                  />
                  <span className="pos-field-val--sub">({discountAmount.toFixed(2)})</span>
                </span>
              </div>
            </div>

            {/* Row 3: Delivery Partner | Rounding */}
            <div className="pos-summary-fields-row">
              <div className="pos-summary-field pos-summary-field--wide">
                <span className="pos-field-label">
                  <svg viewBox="0 0 24 24" width="12" height="12"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  Delivery Partner:
                </span>
                <input
                  type="text"
                  className="pos-field-input pos-field-input--text"
                  value={deliveryPartner}
                  onChange={e => setDeliveryPartner(e.target.value)}
                  placeholder="—"
                />
              </div>
              <div className="pos-summary-field">
                <span className="pos-field-label">Round Off:</span>
                <span className="pos-field-val" style={{ fontWeight: 600, color: autoRounding >= 0 ? '#059669' : '#dc2626' }}>
                  {autoRounding >= 0 ? '+' : ''}{autoRounding.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Grand Total row */}
            <div className="pos-summary-row pos-summary-row--total">
              <span>TOTAL ({itemCount} items)</span>
              <span className="pos-grand-total-amount">${grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* ── Payment Buttons ── */}
          <div className="pos-payment-panel">
            <div className="pos-payment-grid">
               <button
                className="pos-pay-btn pos-pay-btn--blue"
                disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                title="Credit Card (Disabled)"
              >
                <svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                Credit Card
                <kbd>F3</kbd>
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--green"
                onClick={handleQuickCashPay}
                title="Quick Cash Pay — exact amount, auto-print (F2)"
              >
                <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                Quick Cash Pay
                <kbd>F2</kbd>
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--purple"
                onClick={() => handlePayment('split')}
              >
                <svg viewBox="0 0 24 24"><path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" /></svg>
                Split Payment
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--orange"
                onClick={handleHold}
                title="Hold Sale (F4)"
              >
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="10" y1="15" x2="10" y2="9" /><line x1="14" y1="15" x2="14" y2="9" /></svg>
                Hold
                <kbd>F4</kbd>
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--red"
                onClick={handleVoid}
                title="Void Sale (F5)"
              >
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                Void
                <kbd>F5</kbd>
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--gray"
                onClick={() => setShowRefundModal(true)}
              >
                <svg viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                Refund
              </button>
              <button
                className="pos-pay-btn pos-pay-btn--red-outline"
                onClick={handleVoidLine}
                title="Void selected line (Delete)"
              >
                <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                Void Line
                <kbd>Del</kbd>
              </button>
            </div>
          </div>
        </section>

        {/* ══ RIGHT PANEL — Products ══ */}
        <section className="pos-right">
          {/* Search bar */}
          <div className="pos-search-bar">
            <svg className="pos-search-icon" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              className="pos-search-input"
              placeholder="Search by Name, SKU, or scan Barcode  (F1)"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button className="pos-search-clear" onClick={() => { setSearchTerm(''); searchRef.current?.focus(); }}>×</button>
            )}
            <button
              className="pos-add-product-btn"
              onClick={() => setShowAddProductModal(true)}
              title="Add New Product"
            >
              <svg viewBox="0 0 24 24">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Product
            </button>
          </div>

          <div className="pos-right-body">
            {/* ── Category Sidebar ── always visible */}
            <div className="pos-categories">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`pos-cat-btn ${selectedCategory === cat ? 'pos-cat-btn--active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat === 'all' ? '⊞ All' : cat}
                </button>
              ))}
            </div>

            {/* ── Products Grid ── */}
            <div className="pos-products-area">
              {isLoading ? (
                <div className="pos-loading">
                  <div className="pos-spinner" />
                  <p>Loading products…</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="pos-no-products">
                  <svg viewBox="0 0 24 24" width="56" height="56"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <p>No products found</p>
                  <p className="pos-no-products-sub">Try a different search or category</p>
                </div>
              ) : (
                <div className="pos-products-grid">
                  {filteredProducts.map(product => {
                    const price = getPrice(product);
                    const name = product.name || product.product || 'Unknown';
                    const inCart = cart.find(i => i.id === product.id);
                    return (
                      <button
                        key={product.id}
                        className={`pos-product-card ${inCart ? 'pos-product-card--incart' : ''}`}
                        onClick={() => addToCart(product)}
                      >
                        {inCart && <span className="pos-product-badge">{inCart.quantity}</span>}
                        <div className="pos-product-img">
                          {product.image ? (
                            <img src={product.image} alt={name} />
                          ) : (
                            <svg viewBox="0 0 24 24" width="36" height="36" opacity="0.4">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          )}
                        </div>
                        <div className="pos-product-info">
                          <div className="pos-product-name">{name}</div>
                          {product.sku && <div className="pos-product-sku">{product.sku}</div>}
                          <div className="pos-product-price">${price.toFixed(2)}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className="pos-footer">
        <div className="pos-footer-left">
          <button className="pos-footer-exit" onClick={() => navigate('/')} title="Exit POS">
            <svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          </button>
          <span className="pos-footer-support">Customer ID #00021 &nbsp;|&nbsp; Support: #205 490 8820</span>
        </div>

        <div className="pos-footer-center">
          Store Name: <span className="pos-footer-outlet">{location || 'Encore Store'}</span>
        </div>

        <div className="pos-footer-right">
          <button className="pos-footer-btn pos-footer-btn--blue" onClick={() => setShowPriceLookupModal(true)}>Price Lookup</button>
          <button className="pos-footer-btn pos-footer-btn--green" onClick={() => addToast('Online status active', 'success')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}><path d="M5 12.55a11 11 0 0 1 14.08 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" /></svg>
            Online
          </button>
          <button className="pos-footer-btn pos-footer-btn--black" onClick={() => window.location.reload()}>Refresh</button>
          <button className="pos-footer-btn pos-footer-btn--red" onClick={() => addToast('Help coming soon', 'info')}>Help</button>
        </div>
      </footer>

      {/* ══ Customer Panel Drawer ══ */}
      {showCustomerPanel && (
        <div className="pos-drawer-overlay" onClick={() => setShowCustomerPanel(false)}>
          <div className="pos-drawer" onClick={e => e.stopPropagation()}>
            <div className="pos-drawer-header">
              <h3>👤 Customer Panel</h3>
              <button onClick={() => setShowCustomerPanel(false)}>×</button>
            </div>
            <div className="pos-drawer-body">
              <div className="pos-customer-current">
                <div className="pos-customer-avatar">{selectedCustomer.name[0]}</div>
                <div>
                  <div className="pos-customer-dname">{selectedCustomer.name}</div>
                  {selectedCustomer.phone && <div className="pos-customer-phone">{selectedCustomer.phone}</div>}
                  {selectedCustomer.email && <div className="pos-customer-email">{selectedCustomer.email}</div>}
                </div>
              </div>
              <div className="pos-customer-list-header">Switch Customer</div>
              <div className="pos-customer-list">
                {customers.map(c => (
                  <button
                    key={c.id}
                    className={`pos-customer-item ${selectedCustomer.id === c.id ? 'active' : ''}`}
                    onClick={() => { setSelectedCustomer(c); setShowCustomerPanel(false); addToast(`Customer: ${c.name}`, 'success'); }}
                  >
                    <span className="pos-customer-item-ava">{c.name[0]}</span>
                    <div>
                      <div>{c.name}</div>
                      {c.phone && <small>{c.phone}</small>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Hold Sales Modal ══ */}
      {showHeldSalesPanel && (
        <HoldSalesModal
          heldSales={heldSales}
          setHeldSales={setHeldSales}
          onRestore={(sale) => {
            restoreHeld(sale);
            setShowHeldSalesPanel(false);
          }}
          onClose={() => setShowHeldSalesPanel(false)}
        />
      )}

      {/* ══ Payment Modal ══ */}
      {showPaymentModal && (
        <PaymentModal
          total={grandTotal}
          rawTotal={rawTotal}
          type={paymentType}
          customer={selectedCustomer}
          onClose={() => setShowPaymentModal(false)}
          onConfirm={confirmPayment}
          addToast={addToast}
        />
      )}

      {/* ══ Receipt Modal ══ */}
      {showReceipt && lastReceipt && (
        <ReceiptModal
          sale={lastReceipt}
          onClose={() => { setShowReceipt(false); setAutoPrintReceipt(false); }}
          autoPrint={autoPrintReceipt}
        />
      )}

      {/* ══ Open Sales Modal ══ */}
      {showOpenSalesModal && (
        <POSOpenSalesModal 
          onClose={() => setShowOpenSalesModal(false)} 
          onDelete={() => addToast('Sale deleted successfully', 'success')}
          onEdit={(sale) => {
            // Load items to cart
            const restoredCart = sale.items.map(item => ({
              id: item.product_id,
              name: item.product?.name || item.name || `Product ${item.product_id}`,
              price: parseFloat(item.unit_price || item.unit_price_inc_tax || 0),
              quantity: parseFloat(item.quantity || 1),
              unit: item.product?.unit?.actual_name || 'Unit'
            }));
            setCart(restoredCart);
            // Close modal
            setShowOpenSalesModal(false);
            addToast(`Loaded invoice ${sale.invoiceNo} into cart`, 'success');
          }}
        />
      )}

      {/* ══ Age Verification Modal ══ */}
      {ageVerifyProduct && (
        <AgeVerificationModal
          productName={ageVerifyProduct.name || ageVerifyProduct.product}
          onVerify={() => {
            setAgeVerified(true);
            addToCartDirect(ageVerifyProduct);
            setAgeVerifyProduct(null);
          }}
          onOverride={() => {
            setAgeVerified(true);
            addToCartDirect(ageVerifyProduct);
            setAgeVerifyProduct(null);
          }}
          onCancel={() => {
            setAgeVerifyProduct(null);
          }}
        />
      )}

      {/* ══ Add Product Modal ══ */}
      {showAddProductModal && (
        <div className="pos-modal-overlay" onClick={() => setShowAddProductModal(false)}>
          <div className="pos-modal pos-modal--add-product" onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header">
              <h2>Add New Product</h2>
              <button className="pos-modal-close" onClick={() => setShowAddProductModal(false)}>×</button>
            </div>
            <div className="pos-modal-body pos-modal-body--scrollable">
              <AddProduct onSuccess={() => {
                setShowAddProductModal(false);
                loadProducts();
                addToast('Product added successfully!', 'success');
              }} />
            </div>
          </div>
        </div>
      )}

      {/* ══ Add Customer Modal ══ */}
      {showAddCustomerModal && (
        <div className="pos-modal-overlay" onClick={() => setShowAddCustomerModal(false)}>
          <div className="pos-modal pos-modal--add-product" style={{ maxWidth: '1200px' }} onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header">
              <h2>Add Customer</h2>
              <button className="pos-modal-close" onClick={() => setShowAddCustomerModal(false)}>×</button>
            </div>
            <div className="pos-modal-body pos-modal-body--scrollable">
              <AddSupplier 
                initialType="customers"
                onSuccess={(newCustomer) => {
                  setShowAddCustomerModal(false);
                  addToast('Customer added successfully!', 'success');
                  loadCustomers(); // Refresh the dropdown
                  // Automatically select the new customer
                  if (newCustomer) {
                    setSelectedCustomer({
                      id: newCustomer.id,
                      name: newCustomer.name || `${newCustomer.first_name || ''} ${newCustomer.last_name || ''}`.trim(),
                      phone: newCustomer.mobile || newCustomer.contact_no || '',
                      email: newCustomer.email || ''
                    });
                  }
                }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* ══ Edit Customer Modal ══ */}
      {showEditCustomerModal && (
        <div className="pos-modal-overlay" onClick={() => setShowEditCustomerModal(false)}>
          <div className="pos-modal pos-modal--add-product" style={{ maxWidth: '1200px' }} onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header">
              <h2>Edit Customer</h2>
              <button className="pos-modal-close" onClick={() => setShowEditCustomerModal(false)}>×</button>
            </div>
            <div className="pos-modal-body pos-modal-body--scrollable">
              <AddSupplier 
                initialType="customers"
                editData={customerForEdit}
                onSuccess={(updatedCustomer) => {
                  setShowEditCustomerModal(false);
                  addToast('Customer updated successfully!', 'success');
                  loadCustomers(); // Refresh the list
                  // Update current selected customer
                  if (updatedCustomer) {
                    const d = updatedCustomer.data || updatedCustomer;
                    setSelectedCustomer({
                      id: d.id,
                      name: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim(),
                      phone: d.mobile || d.contact_no || '',
                      email: d.email || ''
                    });
                  }
                }} 
              />
            </div>
          </div>
        </div>
      )}
      {showPriceLookupModal && (
        <div className="pos-modal-overlay" onClick={() => { setShowPriceLookupModal(false); setPriceLookupQuery(''); setPriceLookupResults([]); }}>
          <div className="pos-modal" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header" style={{ borderBottom: 'none', paddingBottom: '0' }}>
              <button className="pos-modal-close" onClick={() => { setShowPriceLookupModal(false); setPriceLookupQuery(''); setPriceLookupResults([]); }}>×</button>
            </div>
            <div className="pos-modal-body" style={{ padding: '0 24px 24px 24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <input
                  autoFocus
                  type="text"
                  placeholder="Search by item name, barcode, or category"
                  value={priceLookupQuery}
                  onChange={e => {
                    const q = e.target.value;
                    setPriceLookupQuery(q);
                    if (q.trim().length > 1) {
                      const res = products.filter(p => {
                        const nameMatch = (p.name || p.product || '').toLowerCase().includes(q.toLowerCase());
                        const skuMatch = (p.sku || '').toLowerCase().includes(q.toLowerCase());
                        return nameMatch || skuMatch;
                      });
                      setPriceLookupResults(res);
                    } else {
                      setPriceLookupResults([]);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '15px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    outline: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    borderColor: '#c4b5fd' /* soft purple tint matching the image */
                  }}
                />

                {priceLookupQuery.trim() === '' ? (
                  <div style={{ color: '#64748b', fontSize: '14px', marginTop: '10px' }}>
                    Enter search term to find products
                  </div>
                ) : (
                  <div style={{ width: '100%', maxHeight: '300px', overflowY: 'auto', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {priceLookupResults.length > 0 ? priceLookupResults.map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#f8fafc' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1e293b' }}>{p.name || p.product}</div>
                          {p.sku && <div style={{ fontSize: '12px', color: '#64748b' }}>{p.sku}</div>}
                        </div>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#6366f1' }}>
                          ${getPrice(p).toFixed(2)}
                        </div>
                      </div>
                    )) : (
                      <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No products found match your query.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Edit Cart Item Modal ══ */}
      {editingCartItem && (
        <div className="pos-modal-overlay" onClick={() => setEditingCartItem(null)}>
          <div className="pos-modal" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
            <div className="pos-modal-header" style={{ borderBottom: 'none', paddingBottom: '8px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', textTransform: 'uppercase' }}>
                {editingCartItem.name || editingCartItem.product}
              </h2>
              <button className="pos-modal-close" onClick={() => setEditingCartItem(null)}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="blue" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="pos-modal-body" style={{ padding: '0 20px 20px 20px' }}>
              <form onSubmit={e => {
                e.preventDefault();
                setCart(prev => prev.map(i => i.id === editingCartItem.id ? editingCartItem : i));
                setEditingCartItem(null);
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                  <div style={{ alignSelf: 'end' }}>
                    <div style={{ color: '#84cc16', fontWeight: 'bold', fontSize: '12px', textAlign: 'center' }}>
                      Current Stock: {editingCartItem.quantity_available || '10.00'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#1e293b' }}>Employee</label>
                    <select style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none' }}>
                      <option value="">Select Employee</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ background: '#f3e8ff', border: '1px solid #65a30d', borderRadius: '4px', padding: '10px', textAlign: 'center', color: '#65a30d', fontWeight: 'bold', fontSize: '13px' }}>
                    Sale Price: {editingCartItem.price}
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', padding: '10px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                    WSP: {editingCartItem.price}
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', padding: '10px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                    LPP/PP: {(editingCartItem.price * 0.7).toFixed(2)}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b' }}>Price</label>
                    <input
                      type="number"
                      step="any"
                      value={editingCartItem.price}
                      onChange={e => setEditingCartItem({ ...editingCartItem, price: parseFloat(e.target.value) || 0 })}
                      style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none', textAlign: 'center' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b' }}>Quantity</label>
                    <div style={{ display: 'flex' }}>
                      <button type="button" onClick={() => setEditingCartItem({ ...editingCartItem, quantity: Math.max(1, editingCartItem.quantity - 1) })} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', background: '#e2e8f0', cursor: 'pointer', borderRadius: '4px 0 0 4px' }}>−</button>
                      <input
                        type="number"
                        step="any"
                        value={editingCartItem.quantity}
                        onChange={e => setEditingCartItem({ ...editingCartItem, quantity: parseFloat(e.target.value) || 0 })}
                        style={{ flex: '1', width: '100%', padding: '8px', borderTop: '1px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', borderLeft: 'none', borderRight: 'none', outline: 'none', textAlign: 'center' }}
                      />
                      <button type="button" onClick={() => setEditingCartItem({ ...editingCartItem, quantity: editingCartItem.quantity + 1 })} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', background: '#e2e8f0', cursor: 'pointer' }}>+</button>
                      <span style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderLeft: 'none', background: '#e2e8f0', borderRadius: '0 4px 4px 0', fontSize: '12px' }}>Each</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b' }}>Discount</label>
                      <div style={{ fontSize: '10px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={editingCartItem.discountType === '$' ? { color: '#22c55e', fontWeight: 'bold' } : {}}> $ </span>
                        <div
                          onClick={() => setEditingCartItem({ ...editingCartItem, discountType: editingCartItem.discountType === '$' ? '%' : '$' })}
                          style={{ width: '28px', height: '14px', background: '#cbd5e1', borderRadius: '7px', position: 'relative', cursor: 'pointer' }}
                        >
                          <div style={{ width: '10px', height: '10px', background: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', left: editingCartItem.discountType === '$' ? '2px' : '16px', transition: 'left 0.2s' }} />
                        </div>
                        <span style={editingCartItem.discountType === '%' ? { color: '#22c55e', fontWeight: 'bold' } : {}}> % </span>
                      </div>
                    </div>
                    <input
                      type="number"
                      step="any"
                      value={editingCartItem.discount || 0}
                      onChange={e => setEditingCartItem({ ...editingCartItem, discount: parseFloat(e.target.value) || 0 })}
                      style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none', textAlign: 'center' }}
                    />
                  </div>
                </div>

                <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '13px', marginBottom: '16px', color: '#1e293b' }}>
                  Total <span style={{ marginLeft: '8px' }}>{((editingCartItem.price * editingCartItem.quantity) - (editingCartItem.discount || 0)).toFixed(2)}</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b' }}>Note</label>
                  <textarea
                    value={editingCartItem.note || ''}
                    onChange={e => setEditingCartItem({ ...editingCartItem, note: e.target.value })}
                    placeholder="Note"
                    style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none', resize: 'vertical', minHeight: '60px', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" style={{ background: '#0b0f19', color: '#fff', padding: '10px 16px', borderRadius: '4px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Add To Cart</button>
                  <button type="button" onClick={() => setEditingCartItem(null)} style={{ background: '#c8102e', color: '#fff', padding: '10px 16px', borderRadius: '4px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ══ Sale Refund Modal ══ */}
      {showRefundModal && (
        <SaleRefundModal onClose={() => setShowRefundModal(false)} />
      )}

      {showCouponModal && (
        <DiscountCouponModal 
          code={couponCode}
          setCode={setCouponCode}
          onClose={() => setShowCouponModal(false)}
          onSubmit={() => {
            addToast(`Coupon ${couponCode} submitted!`, 'success');
            setShowCouponModal(false);
          }}
        />
      )}

      {showRegisterDetailsModal && (
        <RegisterDetailsModal 
          isOpen={showRegisterDetailsModal} 
          onClose={() => setShowRegisterDetailsModal(false)}
          userDetails={userData}
        />
      )}
    </div>
  );
};

export default POSPage;
